import React from 'react';
import { CheckSquareIcon, SquareIcon } from 'lucide-react';
import UnifiedBookCard from '../../components/Books/UnifiedBookCard';
import { abbreviateGenre } from '../../utils/genreAbbreviations';
interface BookCardProps {
  book: any;
  isEditMode: boolean;
  toggleBookSelection: (id: string) => void;
  navigateToBookDetail: (id: string) => void;
  onCardClick?: (book: any) => void;
  setHoveredBookId: (id: string | null) => void;
  selectedBooks: string[];
  darkMode: boolean;
  handleDragStart?: (id: string) => void;
  handleDragEnter?: (id: string) => void;
  handleDragEnd?: () => void;
}
// Abbreviate long genre strings so they fit on a single line in the card tag.
// Subgenres resolve through the canonical registry; see utils/genreAbbreviations.
// Maps bookshelf book schema → UnifiedBookCard schema
function normalizeForUnified(book: any) {
  const rawGenres = book.genres ?? (book.genre ? [book.genre] : []);
  return {
    ...book,
    genres: rawGenres.map(abbreviateGenre),
    subgenres: book.subgenres ?? (book.subgenre ? [book.subgenre] : []),
    seriesName: book.seriesName ?? book.series ?? '',
    formats: book.formats ?? (book.format ? [book.format] : []),
    spiceLevel: book.spiceLevel ?? 0,
    darknessLevel: book.darknessLevel ?? 0,
    endingType: book.endingType ?? undefined,
    relationshipType: book.relationshipType !== 'None' ? book.relationshipType : undefined,
    contentWarnings: book.contentWarnings ?? book.triggerWarnings ?? []
  };
}
// Thin wrapper: renders UnifiedBookCard with edit-mode overlay + drag support
export const BookCard: React.FC<BookCardProps> = ({
  book,
  isEditMode,
  toggleBookSelection,
  navigateToBookDetail,
  onCardClick,
  setHoveredBookId,
  selectedBooks,
  darkMode,
  handleDragStart,
  handleDragEnter,
  handleDragEnd
}) => {
  const isSelected = selectedBooks.includes(book.id);
  const normalizedBook = normalizeForUnified(book);
  const handleClick = () => {
    if (isEditMode) {
      toggleBookSelection(book.id);
    } else if (onCardClick) {
      onCardClick(book);
    }
    // If no onCardClick provided, UnifiedBookCard's Link handles navigation
  };
  return <div className={`relative ${isSelected ? 'ring-2 ring-indigo-500 rounded-lg' : ''}`} draggable={isEditMode} onDragStart={() => isEditMode && handleDragStart?.(book.id)} onDragEnter={() => isEditMode && handleDragEnter?.(book.id)} onDragEnd={() => isEditMode && handleDragEnd?.()} onDragOver={e => isEditMode && e.preventDefault()} data-id="element-10683" data-mp="2ak79vdv3lcdw">
      {/* Intercept clicks when modal mode is active */}
      <div className={onCardClick && !isEditMode ? 'cursor-pointer' : ''} onClick={onCardClick && !isEditMode ? handleClick : undefined} data-id="element-10684" data-mp="2pa4slea9d9vp">
        {/* Disable UnifiedBookCard's own Link navigation when onCardClick is set */}
        <div className={onCardClick && !isEditMode ? 'pointer-events-none' : isEditMode ? 'pointer-events-none' : ''} data-id="element-10685" data-mp="1dwstfgpg1ffv">
          <UnifiedBookCard book={normalizedBook} darkMode={darkMode} data-id="element-10686" data-mp="yq3mxxmkd8d4" />
        </div>
      </div>

      {/* Edit mode overlay — checkbox + selection tint */}
      {isEditMode && <div className={`absolute inset-0 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-indigo-500/20' : 'bg-transparent hover:bg-indigo-500/10'}`} style={{
      aspectRatio: '2/3',
      height: 'calc(100% - 60px)'
    }} onClick={handleClick} data-id="element-10687" data-mp="18p2v7f3f31zl">
          <div className="absolute top-2 right-2 z-30" data-id="element-10688" data-mp="1mjls5b0lddai">
            {isSelected ? <CheckSquareIcon className="h-6 w-6 text-indigo-500 bg-white bg-opacity-90 rounded shadow" data-id="element-10689" data-mp="1resnnvjpjboc" /> : <SquareIcon className="h-6 w-6 text-white bg-black bg-opacity-50 rounded" data-id="element-10690" data-mp="3t88il4mtvcp4" />}
          </div>
        </div>}
    </div>;
};